import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {HTTP_STATUS_OK} from '../../../Constants'
import {QueryKeyTypes} from '../../../query-manager/QueryKeyTypes'
import {
    QueryCRUDTypes,
    QueryKeyManager,
} from '../../../query-manager/QueryKeysManager'
import {severityNotification} from '../../notification/NotificationConstant'
import {showNotification} from '../../notification/NotificationService'
import {
    createOrUpdateUserWithRole,
    getDetailUserWithRole,
    getListUserWithRoles,
    getPageUserWithRoles,
} from '../HealthCareEndPoint'

export function setStorageHCPData(id, data) {
    localStorage.setItem(`hcpDetailData-${id}`, JSON.stringify(data))
}

export function getStorageHCPData(id) {
    return localStorage.getItem(`hcpDetailData-${id}`)
        ? JSON.parse(localStorage.getItem(`hcpDetailData-${id}`))
        : {}
}

export function setStorageHCPPage(hospitalId, timezoneId, data) {
    let key = 'hcpPageData'
    if (hospitalId) key = `${key}-${hospitalId}`
    if (timezoneId) key = `${key}-${timezoneId}`
    localStorage.setItem(key, JSON.stringify(data))
}

export function getStorageHCPPage(hospitalId, timezoneId) {
    let key = 'hcpPageData'
    if (hospitalId) key = `${key}-${hospitalId}`
    if (timezoneId) key = `${key}-${timezoneId}`

    const storageHCPPage = localStorage.getItem(key)

    if (!storageHCPPage || storageHCPPage === 'undefined') {
        return {
            content: [],
        }
    }

    try {
        return JSON.parse(storageHCPPage)
    } catch {
        return {
            content: [],
        }
    }
}

export const useListUserWithRoles = (
    requestorId,
    simple,
    rows,
    filterRequest,
    enabled = true,
) => {
    const {healthCareKeys} = QueryKeyManager(QueryKeyTypes.hcp, requestorId)

    return useQuery({
        queryKey: healthCareKeys.list({
            simple,
            rows,
            filterRequest,
        }),
        queryFn: () =>
            getListUserWithRoles(requestorId, rows, simple, filterRequest),
        enabled: !!requestorId && !!enabled,
    })
}

export const usePageUserWithRoles = (
    hospitalId,
    timezoneId,
    requestorId,
    pageNumber,
    pageRows,
    sortField,
    sortOrder,
    filterRequest,
    enabled = true,
) => {
    const {healthCareKeys} = QueryKeyManager(QueryKeyTypes.hcp, requestorId)

    return useQuery({
        queryKey: healthCareKeys.page({
            hospitalId,
            timezoneId,
            pageNumber,
            pageRows,
            sortField,
            sortOrder,
            filterRequest:
                filterRequest && !!hospitalId
                    ? {...filterRequest, hospitalId: hospitalId}
                    : filterRequest,
        }),
        queryFn: () =>
            getPageUserWithRoles(
                requestorId,
                pageNumber,
                pageRows,
                sortField,
                sortOrder,
                filterRequest && !!hospitalId
                    ? {...filterRequest, hospitalId: hospitalId}
                    : {},
            ),
        enabled: !!requestorId && !!enabled,
        keepPreviousData: true,
        placeholderData:
            pageNumber === 0 && pageRows === 10
                ? getStorageHCPPage(hospitalId, timezoneId)
                : {},
    })
}

export const useHealthCareCRUD = (type, requestorId) => {
    const {healthCareKeys} = QueryKeyManager(QueryKeyTypes.hcp, requestorId)

    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: data =>
            type === QueryCRUDTypes.create || type === QueryCRUDTypes.update
                ? createOrUpdateUserWithRole(requestorId, data)
                : data,
        onSuccess: (data, variables) => {
            // Data is the response from the server
            // Variables is the data that was passed to the mutationFn
            if (data === HTTP_STATUS_OK || data?.id) {
                if (
                    type === QueryCRUDTypes.create ||
                    type === QueryCRUDTypes.update
                ) {
                    showNotification(
                        severityNotification.success,
                        'Health Care Practitioner Update Successfully !!',
                    )
                }
            }
        },
    })
}

export const useDetailUserWithRole = (requestorId, id, enabled = true) => {
    const {healthCareKeys} = QueryKeyManager(QueryKeyTypes.hcp, requestorId)
    return useQuery({
        queryKey: healthCareKeys.detail({requestorId, id}),
        queryFn: () => getDetailUserWithRole(requestorId, id),
        enabled: !!requestorId && !!id && !!enabled,
    })
}
